import {
  type Alignment,
  EMPTY,
  type Grid,
  type Cell,
  alignmentFlags,
  cloneGrid,
  createGrid,
  findAlignments,
  findMove,
  gridSize,
  isAdjacent,
  swapCells,
} from "./board";
import {
  type BoardSize,
  DEFAULT_BOARD_SIZE,
  FIRST_BONUS_AT,
  GAME_OVER_DELAY_MS,
  GameMode,
  HINT_GEM_BOUNCE_MS,
  HINT_TIMEOUT_MS,
  NB_BONUS_GEMS,
  NUM_GEM_TYPES,
  TICK_MS,
  TOTAL_STEPS_FOR_TIMER,
} from "./constants";

/**
 * Port of the Gweled game engine (board_engine.c).
 *
 * The engine owns all game rules and the 50 ms state machine. It never touches the DOM or
 * WebGL: every visual change goes through a {@link GameView}, so the same rules can be tested
 * headlessly.
 */

export type GemEffect = "none" | "blink" | "bounce";
export type SoundEffect = "click" | "swap" | "explode";
export type PressResult = "accepted" | "resumed" | "ignored";

export interface Gem {
  readonly id: number;
  /** Logical column. Always matches the cell the gem belongs to. */
  x: number;
  /** Logical row. Can be negative for gems that have not fallen into place yet. */
  y: number;
  readonly type: number;
  /** True while an animation that the state machine waits for is running. */
  animating: boolean;
}

export interface GameView {
  addGem(gem: Gem): void;
  clearGems(): void;
  /** Glides a gem to a new cell (swap). `done` must be called when the animation ends. */
  moveGem(gem: Gem, x: number, y: number, done: () => void): void;
  /** Falls a gem down its column after a delay. */
  fallGem(gem: Gem, toY: number, delayMs: number, durationMs: number, done: () => void): void;
  /** Start-of-game drop-in animation. Does not block the state machine. */
  dropGem(gem: Gem, toY: number, delayMs: number): void;
  /** Pops a gem and removes it. `done` must be called once it is gone. */
  destroyGem(gem: Gem, done: () => void): void;
  setGemEffect(gem: Gem, effect: GemEffect): void;
  setGemsOpacity(alpha: number): void;
  setGemsVisible(visible: boolean): void;
  setEffectsVisible(visible: boolean): void;

  createCursor(x: number, y: number): number;
  moveCursor(cursorId: number, x: number, y: number): void;
  removeCursor(cursorId: number): void;
  fadeCursor(cursorId: number): void;

  /** Floating score text. (cx, cy) is the centre in board cell units. */
  showScorePopup(text: string, cx: number, cy: number): void;
  /** Full-board message. `lifetimeMs` of 0 keeps it until `clearMessages`. */
  showMessage(text: string, lifetimeMs: number): void;
  clearMessages(): void;

  setScore(value: number): void;
  setProgress(fraction: number): void;
  setProgressText(text: string): void;
  setPauseLabel(paused: boolean): void;
  setPauseVisible(visible: boolean): void;
  playSound(effect: SoundEffect): void;
}

export interface EngineOptions {
  view: GameView;
  /** Returns a number in [0, 1). Defaults to Math.random. */
  random?: () => number;
  onGameOver?: (score: number, mode: GameMode) => void;
}

export interface SavedGame {
  mode: GameMode;
  score: number;
  totalGemsRemoved: number;
  bonusMultiply: number;
  previousBonusAt: number;
  nextBonusAt: number;
  level: number;
  stepsForTimer: number;
  board: number[][];
}

/**
 * Board phases. Player input is separate: a selection and a swap can happen in any phase, as long
 * as the gems involved have settled.
 */
const State = {
  /** Nothing is waiting to be cleared. Moves are accepted, even while gems are still gliding. */
  Idle: 0,
  /** Waits for every gem to stop moving, then looks for alignments (or a level bonus). */
  Resolving: 1,
  /** Waits for the cleared gems to finish popping, then refills the board and drops gems in. */
  Refilling: 2,
} as const;
type State = (typeof State)[keyof typeof State];

const LAYER_DIM_GAME_OVER = 128 / 255;

interface Timer {
  readonly id: number;
  remainingMs: number;
  readonly callback: () => void;
}

export class Engine {
  mode: GameMode = GameMode.Normal;
  running = false;
  paused = false;
  hintsOff = false;

  score = 0;
  private currentScore = 0;
  private scorePerMove = 0;
  private totalGemsRemoved = 0;
  private bonusMultiply = 3;
  private level = 1;
  private previousBonusAt = 0;
  private nextBonusAt = FIRST_BONUS_AT;
  private stepsForTimer = FIRST_BONUS_AT / TOTAL_STEPS_FOR_TIMER;
  /** Set while cascades or level bonuses should not add to the score. */
  private doNotScore = false;

  private state: State = State.Idle;
  private width = DEFAULT_BOARD_SIZE.width;
  private height = DEFAULT_BOARD_SIZE.height;
  private types: Grid = createGrid(this.width, this.height, () => EMPTY);
  private gems: (Gem | null)[][] = createGrid<Gem | null>(this.width, this.height, () => null);
  private nbOfTiles: number[] = Array.from({ length: NUM_GEM_TYPES }, () => 0);
  private alignments: Alignment[] = [];
  private allGems = new Set<Gem>();

  /** One pending click. A newer click replaces it, so only the latest press or release counts. */
  private gemClicked = false;
  private xClick = 0;
  private yClick = 0;
  private lastPress: Cell | null = null;
  /** True while a gem is selected, waiting for a neighbour to swap with. */
  private selected = false;
  private x1 = 0;
  private y1 = 0;
  private cursorId: number | null = null;

  private timeSlice = 0;
  private hintsActive = false;
  private hintElapsedMs = 0;
  private bouncingGem: Gem | null = null;
  private bounceTimerId = 0;

  private progressText = "";
  private progressBeforePause: string | null = null;

  private timers: Timer[] = [];
  private nextTimerId = 1;
  private nextGemId = 1;

  private readonly view: GameView;
  private readonly random: () => number;
  private readonly onGameOver: (score: number, mode: GameMode) => void;

  constructor(options: EngineOptions) {
    this.view = options.view;
    this.random = options.random ?? Math.random;
    this.onGameOver = options.onGameOver ?? (() => {});
  }

  // ---------------------------------------------------------------------------------------------
  // Public API used by the UI
  // ---------------------------------------------------------------------------------------------

  /** Starts a fresh game in `mode` on a board of `size` (gweled_start_new_game). */
  newGame(mode: GameMode, size: BoardSize = DEFAULT_BOARD_SIZE): void {
    this.resetVisuals();
    this.allocateBoard(size);
    this.mode = mode;
    this.paused = false;
    this.score = 0;
    this.currentScore = 0;
    this.scorePerMove = 0;
    this.bonusMultiply = 3;
    this.level = 1;
    this.previousBonusAt = 0;
    this.nextBonusAt = FIRST_BONUS_AT;
    this.stepsForTimer = FIRST_BONUS_AT / TOTAL_STEPS_FOR_TIMER;

    if (mode === GameMode.Timed) {
      this.totalGemsRemoved = FIRST_BONUS_AT / 2;
      this.setProgress(0.5);
    } else {
      this.totalGemsRemoved = 0;
      this.setProgress(0);
    }

    if (mode !== GameMode.Endless) {
      this.setProgressText("Level 1");
    }

    this.setHints(false);
    this.view.setScore(0);
    this.fillNewBoard();
    this.fallIntoPlace(true);

    this.running = true;
    this.state = State.Idle;
    this.gemClicked = false;
    this.selected = false;
  }

  /** Aborts the current game and clears the board (gweled_stop_game). */
  stop(): void {
    this.running = false;
    this.paused = false;
    this.setHints(false);
    this.state = State.Idle;
    this.gemClicked = false;
    this.selected = false;
    this.stopBouncing();
    this.resetVisuals();
    this.score = 0;
    this.currentScore = 0;
    this.setProgressText("");
    this.setProgress(0);
    this.view.setScore(0);
  }

  /** Handles a primary-button press or touch start on a cell (board_input_event). */
  press(x: number, y: number): PressResult {
    if (this.running && this.paused) {
      // Any click while paused resumes the game and is not treated as a move.
      this.setPaused(false);
      return "resumed";
    }
    if (this.paused) return "ignored";

    this.stopBouncing();
    this.lastPress = { x, y };
    this.xClick = x;
    this.yClick = y;
    this.gemClicked = true;
    this.view.playSound("click");
    return "accepted";
  }

  /** Handles a release on a cell. A release on a different cell than the press is a drag. */
  release(x: number, y: number): void {
    if (this.paused) return;
    this.stopBouncing();
    const press = this.lastPress;
    if (press && press.x === x && press.y === y) return;
    this.xClick = x;
    this.yClick = y;
    this.gemClicked = true;
  }

  /** The gem the player has selected and not yet moved, if any. */
  get selectedCell(): { x: number; y: number } | null {
    if (!this.running || this.paused || !this.selected) return null;
    return { x: this.x1, y: this.y1 };
  }

  /** Cancels a pending selection, like clicking the selected gem again. */
  cancelSelection(): void {
    if (!this.paused && this.selected) {
      this.press(this.x1, this.y1);
    }
  }

  /** Pauses or resumes a running game (on_pause_activate_cb). */
  togglePause(): void {
    if (!this.running) return;
    this.setPaused(!this.paused);
  }

  /** Called when the window loses focus (timed mode pauses, like the original). */
  pauseIfTimed(): void {
    if (this.running && this.mode === GameMode.Timed && !this.paused) {
      this.setPaused(true);
    }
  }

  setHintsEnabled(enabled: boolean): void {
    this.hintsOff = !enabled;
    this.setHints(enabled);
  }

  snapshot(): SavedGame {
    return {
      mode: this.mode,
      score: this.score,
      totalGemsRemoved: this.totalGemsRemoved,
      bonusMultiply: this.bonusMultiply,
      previousBonusAt: this.previousBonusAt,
      nextBonusAt: this.nextBonusAt,
      level: this.level,
      stepsForTimer: this.stepsForTimer,
      board: cloneGrid(this.types),
    };
  }

  restore(saved: SavedGame): void {
    this.resetVisuals();
    this.allocateBoard(gridSize(saved.board));
    this.mode = saved.mode;
    this.score = saved.score;
    this.currentScore = saved.score;
    this.totalGemsRemoved = saved.totalGemsRemoved;
    this.bonusMultiply = saved.bonusMultiply;
    this.previousBonusAt = saved.previousBonusAt;
    this.nextBonusAt = saved.nextBonusAt;
    this.level = saved.level;
    this.stepsForTimer = saved.stepsForTimer;
    this.view.setScore(this.score);

    if (this.mode !== GameMode.Endless) {
      this.setProgress(this.progressFraction());
      this.setProgressText(`Level ${this.level}`);
    }

    this.types = cloneGrid(saved.board);
    this.nbOfTiles.fill(0);
    for (let x = 0; x < this.width; x++) {
      for (let y = 0; y < this.height; y++) {
        this.nbOfTiles[this.types[x][y]]++;
      }
    }
    for (let x = 0; x < this.width; x++) {
      for (let y = 0; y < this.height; y++) {
        this.gems[x][y] = this.createGem(x, y, this.types[x][y]);
      }
    }

    this.doNotScore = false;
    this.running = true;
    this.paused = false;
    this.gemClicked = false;
    this.selected = false;
    this.state = State.Resolving;
    this.view.setPauseLabel(false);
  }

  /** Enables or disables hints. Starts the 15 second timer when enabling. */
  private setHints(enabled: boolean): void {
    if (enabled) {
      if (!this.hintsActive) {
        this.hintsActive = true;
        this.hintElapsedMs = 0;
      }
    } else {
      this.hintsActive = false;
    }
  }

  // ---------------------------------------------------------------------------------------------
  // Clock
  // ---------------------------------------------------------------------------------------------

  /** Advances the engine by one 50 ms tick (board_engine_loop). */
  tick(): void {
    this.timeSlice++;
    this.runTimers(TICK_MS);

    if (this.currentScore < this.score) {
      this.currentScore += 10;
      this.view.setScore(this.currentScore);
    }

    if (this.mode === GameMode.Timed && this.running && !this.paused && this.timeSlice % 25 === 0) {
      this.totalGemsRemoved -= this.stepsForTimer;
      if (this.totalGemsRemoved <= this.previousBonusAt) {
        this.timeUp();
      }
      this.setProgress(this.progressFraction());
    }

    if (this.hintsActive && this.gemClicked) {
      this.setHints(false);
    }

    if (this.hintsActive) {
      this.hintElapsedMs += TICK_MS;
      if (this.hintElapsedMs >= HINT_TIMEOUT_MS) {
        this.hintElapsedMs = 0;
        this.fireHint();
      }
    }

    this.step();

    if (
      this.state === State.Idle &&
      !this.selected &&
      !this.gemClicked &&
      !this.hintsActive &&
      !this.hintsOff &&
      this.running
    ) {
      this.setHints(true);
    }
  }

  // ---------------------------------------------------------------------------------------------
  // State machine (the switch in board_engine_loop)
  // ---------------------------------------------------------------------------------------------

  /** One engine step: advance the board, then handle the pending click. */
  private step(): void {
    this.stepBoard();
    this.handleClick();
  }

  /**
   * Advances the board after a swap or a clear. Each phase starts only once no gem is moving, so
   * a refill never shifts a gem that is still in transit.
   */
  private stepBoard(): void {
    if (this.state === State.Idle || this.anyGemAnimating()) return;

    if (this.state === State.Refilling) {
      this.refillBoard(true);
      this.fallIntoPlace(false);
      this.state = State.Resolving;
    } else {
      this.resolveBoard();
    }
  }

  /**
   * Handles the pending click: selects a gem, deselects it, or swaps it with the selected gem. A
   * click waits while its own gem is moving, so other gems can still be selected and swapped.
   */
  private handleClick(): void {
    if (!this.gemClicked) return;
    const x = this.xClick;
    const y = this.yClick;
    if (this.isCellBusy(x, y)) return;
    if (this.selected && this.isCellBusy(this.x1, this.y1)) return;
    this.gemClicked = false;

    if (!this.selected) {
      this.selectGem(x, y);
    } else if (x === this.x1 && y === this.y1) {
      this.deselectGem();
    } else if (isAdjacent(this.x1, this.y1, x, y)) {
      this.swapWithSelected(x, y);
    } else {
      this.setGemEffectAt(this.x1, this.y1, "none");
      this.selectGem(x, y);
    }
  }

  private selectGem(x: number, y: number): void {
    this.selected = true;
    this.x1 = x;
    this.y1 = y;
    this.setGemEffectAt(x, y, "blink");
    this.replaceCursor(x, y);
  }

  private deselectGem(): void {
    this.selected = false;
    this.removeCursor();
    this.setGemEffectAt(this.x1, this.y1, "none");
  }

  /**
   * Swaps the selected gem with a neighbour. A legal swap is applied to the board right away and
   * the gems glide into place. An illegal one glides out and back, and the board does not change.
   */
  private swapWithSelected(x2: number, y2: number): void {
    const x1 = this.x1;
    const y1 = this.y1;
    const first = this.gems[x1][y1];
    const second = this.gems[x2][y2];
    if (!first || !second) return;

    this.selected = false;
    this.setGemEffectAt(x1, y1, "none");
    this.view.playSound("swap");
    if (this.cursorId !== null) this.view.moveCursor(this.cursorId, x2, y2);
    this.fadeCursor();

    if (this.isLegalSwap(x1, y1, x2, y2)) {
      this.swapCellsOnBoard(x1, y1, x2, y2);
      this.glideGem(first, x2, y2);
      this.glideGem(second, x1, y1);
      this.scorePerMove = 0;
      if (this.state === State.Idle) this.state = State.Resolving;
    } else {
      this.glideGem(first, x2, y2, () => this.glideGem(first, x1, y1));
      this.glideGem(second, x1, y1, () => this.glideGem(second, x2, y2));
    }
  }

  /** True when the swap would put a line through either cell. The board is left unchanged. */
  private isLegalSwap(x1: number, y1: number, x2: number, y2: number): boolean {
    swapCells(this.types, x1, y1, x2, y2);
    const legal = this.isPartOfAlignment(x1, y1) || this.isPartOfAlignment(x2, y2);
    swapCells(this.types, x1, y1, x2, y2);
    return legal;
  }

  /** Resolving phase: no gem is moving. Clears the alignments found, or ends the phase. */
  private resolveBoard(): void {
    if (this.totalGemsRemoved >= this.nextBonusAt && this.mode !== GameMode.Endless) {
      this.levelBonus();
      return;
    }

    if (this.checkForAlignments()) {
      this.takeDownDeletedGems();
      this.removeGemsAndUpdateScore();
      this.view.playSound("explode");

      if (this.mode !== GameMode.Endless) {
        this.setProgress(this.progressFraction());
      }

      this.state = State.Refilling;
      return;
    }

    if (findMove(this.types) !== null) {
      this.doNotScore = false;
      this.state = State.Idle;
      return;
    }

    if (this.mode === GameMode.Endless || this.mode === GameMode.Timed) {
      // No moves: reshuffle the board instead of ending the game.
      this.view.showMessage("No moves left!", 2000);
      this.clearGems();
      this.fillNewBoard();
      this.fallIntoPlace(false);
    } else {
      this.noMovesGameOver();
    }
  }

  private levelBonus(): void {
    this.previousBonusAt = this.nextBonusAt;
    this.nextBonusAt *= 2;

    if (this.mode === GameMode.Timed) {
      this.stepsForTimer =
        Math.floor((this.nextBonusAt - this.previousBonusAt) / TOTAL_STEPS_FOR_TIMER) + 1;
    }

    this.bonusMultiply++;
    this.level++;
    this.setProgressText(`Level ${this.level}`);
    this.view.showMessage(`Bonus x${this.bonusMultiply >> 1}`, 2000);

    this.deleteGemsForBonus();
    this.takeDownDeletedGems();
    this.removeGemsAndUpdateScore();

    if (this.mode === GameMode.Timed) {
      this.totalGemsRemoved = Math.floor((this.nextBonusAt + this.previousBonusAt) / 2);
    }

    this.setProgress(this.progressFraction());
    // Matches that come from the refill after a bonus are not scored.
    this.doNotScore = true;
    this.state = State.Refilling;
  }

  // ---------------------------------------------------------------------------------------------
  // Rules
  // ---------------------------------------------------------------------------------------------

  private progressFraction(): number {
    const span = this.nextBonusAt - this.previousBonusAt;
    if (this.totalGemsRemoved <= this.nextBonusAt) {
      return (this.totalGemsRemoved - this.previousBonusAt) / span;
    }
    return 1;
  }

  private timeUp(): void {
    this.view.showMessage("Time's up!", 0);
    this.view.setGemsOpacity(LAYER_DIM_GAME_OVER);
    this.setGemEffectAt(this.xClick, this.yClick, "none");
    this.finishGame();
  }

  private noMovesGameOver(): void {
    this.view.showMessage("No moves left!", 0);
    this.view.setGemsOpacity(LAYER_DIM_GAME_OVER);
    this.finishGame();
  }

  private finishGame(): void {
    this.view.setEffectsVisible(false);
    this.view.setPauseVisible(false);
    this.running = false;
    this.paused = true;
    this.state = State.Idle;
    this.selected = false;

    if (this.score > 0) {
      const { score, mode } = this;
      this.schedule(GAME_OVER_DELAY_MS, () => this.onGameOver(score, mode));
    }
  }

  private checkForAlignments(): boolean {
    this.alignments = findAlignments(this.types);
    return this.alignments.length !== 0;
  }

  private removeGemsAndUpdateScore(): void {
    for (const alignment of this.alignments) {
      this.deleteAlignmentFromBoard(alignment);
    }
  }

  /** delete_alignment_from_board: clears the cells and scores the run. */
  private deleteAlignmentFromBoard(alignment: Alignment): void {
    const { x, y, length } = alignment;
    let gemsRemoved = 0;
    let popupX: number;
    let popupY: number;

    if (alignment.horizontal) {
      popupX = x + length / 2;
      popupY = y + 0.5;
      for (let i = x; i < x + length; i++) {
        if (this.types[i][y] !== EMPTY) {
          gemsRemoved++;
          this.nbOfTiles[this.types[i][y]]--;
          this.types[i][y] = EMPTY;
        }
      }
    } else {
      popupX = x + 0.5;
      popupY = y + length / 2;
      for (let i = y; i < y + length; i++) {
        if (this.types[x][i] !== EMPTY) {
          gemsRemoved++;
          this.nbOfTiles[this.types[x][i]]--;
          this.types[x][i] = EMPTY;
        }
      }
    }

    let totalScore: number;
    if (length === 1) {
      // Level bonus gems are worth a flat 10 points.
      totalScore = 10;
    } else {
      totalScore = 10 * (this.bonusMultiply >> 1) * (length - 2) + this.scorePerMove;
      if (this.doNotScore) this.scorePerMove = totalScore;
    }

    if (!this.doNotScore) {
      this.totalGemsRemoved += gemsRemoved;
      this.score += totalScore;
      this.view.showScorePopup(String(totalScore), popupX, popupY);
    }
  }

  private deleteGemsForBonus(): void {
    this.alignments = [];
    for (let i = 0; i < NB_BONUS_GEMS; i++) {
      // Matches the original's randRange(0, 7), which never picks the last row or column.
      this.alignments.push({
        x: this.randomInt(0, this.width - 1),
        y: this.randomInt(0, this.height - 1),
        horizontal: true,
        length: 1,
      });
    }
  }

  private takeDownDeletedGems(): void {
    for (const alignment of this.alignments) {
      const { x, y, length } = alignment;
      if (alignment.horizontal) {
        for (let i = x; i < x + length; i++) {
          this.destroyGemAt(i, y);
        }
      } else {
        for (let i = y; i < y + length; i++) {
          this.destroyGemAt(x, i);
        }
      }
    }
  }

  private destroyGemAt(x: number, y: number): void {
    const gem = this.gems[x][y];
    if (gem) {
      gem.animating = true;
      this.view.destroyGem(gem, () => {
        gem.animating = false;
        this.allGems.delete(gem);
      });
    }
    this.gems[x][y] = null;
  }

  private refillBoard(withGems: boolean): void {
    let lastTile = EMPTY;
    let sameTileCount = 1;

    for (let i = 0; i < this.width; i++) {
      for (let j = 0; j < this.height; j++) {
        if (this.types[i][j] !== EMPTY) continue;

        for (let k = j; k > 0; k--) {
          this.types[i][k] = this.types[i][k - 1];
          if (withGems) this.gems[i][k] = this.gems[i][k - 1];
        }
        this.types[i][0] = this.newTile();

        // Never drop three equal gems in a row.
        if (lastTile === this.types[i][0]) {
          sameTileCount++;
        } else {
          sameTileCount = 1;
        }
        if (sameTileCount >= 3) {
          do {
            this.types[i][0] = this.newTile();
          } while (lastTile === this.types[i][0]);
        }

        lastTile = this.types[i][0];
        this.nbOfTiles[this.types[i][0]]++;

        if (withGems) {
          // New gems start above the board, just above the gem that is falling into their column.
          const neighbour = j > 0 ? this.gems[i][1] : null;
          const above = neighbour ? neighbour.y - 1 : -1;
          this.gems[i][0] = this.createGem(i, above, this.types[i][0]);
        }
      }
    }
  }

  /** gweled_fill_new_board: a fresh random board with no alignments already on it. */
  private fillNewBoard(): void {
    this.nbOfTiles.fill(0);
    this.gems = createGrid<Gem | null>(this.width, this.height, () => null);
    for (let i = 0; i < this.width; i++) {
      for (let j = 0; j < this.height; j++) {
        this.types[i][j] = this.newTile();
        this.nbOfTiles[this.types[i][j]]++;
      }
    }

    this.doNotScore = true;
    while (this.checkForAlignments()) {
      this.removeGemsAndUpdateScore();
      this.refillBoard(false);
    }
    this.doNotScore = false;

    for (let i = 0; i < this.width; i++) {
      for (let j = 0; j < this.height; j++) {
        this.gems[i][j] = this.createGem(i, j - this.height, this.types[i][j]);
      }
    }
  }

  /** get_new_tile: biases new gems toward the least common type or away from the most common. */
  private newTile(): number {
    const counts = this.nbOfTiles;
    let minIndex = 0;
    let maxIndex = 0;
    let min = counts[0];
    let max = counts[0];
    for (let i = 0; i < NUM_GEM_TYPES; i++) {
      if (counts[i] < min) {
        min = counts[i];
        minIndex = i;
      }
      if (counts[i] > max) {
        max = counts[i];
        maxIndex = i;
      }
    }

    if (this.randomInt(0, 2) === 0) {
      return minIndex;
    }
    return (maxIndex + this.randomInt(1, NUM_GEM_TYPES)) % NUM_GEM_TYPES;
  }

  private isPartOfAlignment(x: number, y: number): boolean {
    return alignmentFlags(this.types, x, y) !== 0;
  }

  // ---------------------------------------------------------------------------------------------
  // Gem objects and animation
  // ---------------------------------------------------------------------------------------------

  private createGem(x: number, y: number, type: number): Gem {
    const gem: Gem = { id: this.nextGemId++, x, y, type, animating: false };
    this.allGems.add(gem);
    this.view.addGem(gem);
    return gem;
  }

  private clearGems(): void {
    this.allGems.clear();
    this.view.clearGems();
    this.gems = createGrid<Gem | null>(this.width, this.height, () => null);
  }

  /** Sizes the board arrays for a new game. Gems are cleared separately by `resetVisuals`. */
  private allocateBoard(size: BoardSize): void {
    this.width = size.width;
    this.height = size.height;
    this.types = createGrid(this.width, this.height, () => EMPTY);
    this.gems = createGrid<Gem | null>(this.width, this.height, () => null);
  }

  private resetVisuals(): void {
    this.stopBouncing();
    this.timers = [];
    this.clearGems();
    this.removeCursor();
    this.view.clearMessages();
    this.view.setGemsOpacity(1);
    this.view.setGemsVisible(true);
    this.view.setEffectsVisible(true);
  }

  /** A cell is busy while its gem is moving, or while it is empty and waiting for a refill. */
  private isCellBusy(x: number, y: number): boolean {
    const gem = this.gems[x]?.[y];
    return !gem || gem.animating;
  }

  private anyGemAnimating(): boolean {
    for (const gem of this.allGems) {
      if (gem.animating) return true;
    }
    return false;
  }

  /** Glides a gem to a cell. `done` runs once it has arrived. */
  private glideGem(gem: Gem, x: number, y: number, done?: () => void): void {
    gem.x = x;
    gem.y = y;
    gem.animating = true;
    this.view.moveGem(gem, x, y, () => {
      gem.animating = false;
      done?.();
    });
  }

  private swapCellsOnBoard(x1: number, y1: number, x2: number, y2: number): void {
    swapCells(this.types, x1, y1, x2, y2);
    const tmp = this.gems[x1][y1];
    this.gems[x1][y1] = this.gems[x2][y2];
    this.gems[x2][y2] = tmp;
  }

  /** Falls gems into empty cells. Only called once no gem is moving, so the falls start together. */
  private fallIntoPlace(newBoard: boolean): void {
    let maxHeight = 0;
    for (let i = this.width - 1; i >= 0; i--) {
      let delayIncrement = 0;
      for (let j = this.height - 1; j >= 0; j--) {
        const gem = this.gems[i][j];
        if (!gem || gem.y === j) continue;

        if (maxHeight < j) maxHeight = j;

        if (newBoard) {
          gem.y = j;
          this.view.dropGem(gem, j, i * 100 + (this.height - j) * 50);
        } else {
          gem.y = j;
          gem.animating = true;
          this.view.fallGem(gem, j, delayIncrement * 25, 100 + 20 * maxHeight, () => {
            gem.animating = false;
          });
        }
        delayIncrement++;
      }
    }
  }

  // ---------------------------------------------------------------------------------------------
  // Cursor, effects, and hints
  // ---------------------------------------------------------------------------------------------

  private replaceCursor(x: number, y: number): void {
    this.removeCursor();
    this.cursorId = this.view.createCursor(x, y);
  }

  private removeCursor(): void {
    if (this.cursorId !== null) {
      this.view.removeCursor(this.cursorId);
      this.cursorId = null;
    }
  }

  private fadeCursor(): void {
    if (this.cursorId !== null) {
      this.view.fadeCursor(this.cursorId);
      this.cursorId = null;
    }
  }

  private setGemEffectAt(x: number, y: number, effect: GemEffect): void {
    const gem = this.gems[x]?.[y];
    if (gem) this.view.setGemEffect(gem, effect);
  }

  private fireHint(): void {
    if (!this.running) return;
    const move = findMove(this.types);
    if (!move) return;
    const gem = this.gems[move.x][move.y];
    // A gem that is moving keeps its glide; the hint waits for a settled gem.
    if (!gem || gem.animating) return;

    this.stopBouncing();
    this.bouncingGem = gem;
    this.view.setGemEffect(gem, "bounce");
    this.bounceTimerId = this.schedule(HINT_GEM_BOUNCE_MS, () => {
      this.bounceTimerId = 0;
      this.stopBouncing();
    });
  }

  private stopBouncing(): void {
    if (this.bouncingGem) {
      this.view.setGemEffect(this.bouncingGem, "none");
      this.bouncingGem = null;
    }
    if (this.bounceTimerId) {
      this.cancelTimer(this.bounceTimerId);
      this.bounceTimerId = 0;
    }
  }

  // ---------------------------------------------------------------------------------------------
  // Pause and HUD
  // ---------------------------------------------------------------------------------------------

  private setPaused(paused: boolean): void {
    this.paused = paused;
    if (paused) {
      this.view.setPauseLabel(true);
      this.view.showMessage("Paused", 0);
      this.progressBeforePause = this.progressText;
      this.setProgressText("Paused");
      this.view.setGemsVisible(false);
      this.view.setEffectsVisible(false);
      this.setHints(false);
    } else {
      this.view.setPauseLabel(false);
      if (this.progressBeforePause !== null) {
        this.setProgressText(this.progressBeforePause);
        this.progressBeforePause = null;
      }
      this.view.setGemsVisible(true);
      this.view.setEffectsVisible(true);
      this.view.clearMessages();
    }
  }

  private setProgress(fraction: number): void {
    this.view.setProgress(Math.min(1, Math.max(0, fraction)));
  }

  private setProgressText(text: string): void {
    this.progressText = text;
    this.view.setProgressText(text);
  }

  // ---------------------------------------------------------------------------------------------
  // Timers and randomness
  // ---------------------------------------------------------------------------------------------

  private schedule(delayMs: number, callback: () => void): number {
    const id = this.nextTimerId++;
    this.timers.push({ id, remainingMs: delayMs, callback });
    return id;
  }

  private cancelTimer(id: number): void {
    this.timers = this.timers.filter((timer) => timer.id !== id);
  }

  private runTimers(elapsedMs: number): void {
    const due: Timer[] = [];
    for (const timer of this.timers) {
      timer.remainingMs -= elapsedMs;
      if (timer.remainingMs <= 0) due.push(timer);
    }
    if (due.length === 0) return;

    this.timers = this.timers.filter((timer) => timer.remainingMs > 0);
    for (const timer of due) {
      timer.callback();
    }
  }

  /** Integer in [min, max), like g_rand_int_range. */
  private randomInt(min: number, max: number): number {
    return min + Math.floor(this.random() * (max - min));
  }
}
